import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import { ThemeProvider } from './context/ThemeContext';
import { NativeAppProvider } from './contexts/NativeAppContext';
import LandingPageWithForm from './pages/LandingPageWithForm';
import RigReadyPage from './pages/RigReadyPage';
import QuizPage from './pages/QuizPage';
import RVMatchApplicationForm from './pages/RVMatchApplicationForm';
import ProductCheckoutPage from './pages/ProductCheckoutPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import MyMatchesPage from './pages/MyMatchesPage';
import MyQuizPage from './pages/MyQuizPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AdminSupportPage from './pages/AdminSupportPage';
import AdminDealerEmailPage from './pages/AdminDealerEmailPage';
import VIPSuccessPage from './pages/VIPSuccessPage';
import EbookViewerPage from './pages/EbookViewerPage';
import RVTravelGuideEbook from './pages/RVTravelGuideEbook';
import RVBuyingGuideEbook from './pages/RVBuyingGuideEbook';
import EbookSqueezePage from './pages/EbookSqueezePage';
import EbookSuccessPage from './pages/EbookSuccessPage';
import UserDashboard from './pages/UserDashboard';
import DealerDashboard from './pages/DealerDashboardNew';
import DealerRegister from './pages/DealerRegister';
import DealerAuth from './pages/DealerAuth';
import DealerLandingPage from './pages/DealerLandingPage';
import DealerPlansPage from './pages/DealerPlansPage';
import DealerLinkRedirect from './pages/DealerLinkRedirect';
import AdminDashboard from './pages/AdminDashboard';
import AdminCommerceCenter from './pages/AdminCommerceCenter';
import InventoryManager from './pages/InventoryManager';
import CRMHub from './pages/CRMHub';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import RVMatchAssist from './pages/RVMatchAssist';
import WebResearch from './pages/WebResearch';
import VIPDashboard from './pages/VIPDashboard';
import NegotiationCoachPage from './pages/NegotiationCoachPage';
import DealAnalyzerPage from './pages/DealAnalyzerPage';
import CostCalculatorPage from './pages/CostCalculatorPage';
import MyToolLauncher from './pages/MyToolLauncher';
import ToolSuccessPage from './pages/ToolSuccessPage';
import FavoritesPage from './pages/FavoritesPage';
import CustomerIntakeForm from './pages/CustomerIntakeForm';
import FreeToolPage from './pages/FreeToolPage';
import ShareToolsPage from './pages/ShareToolsPage';
import ToolsBundlePage from './pages/ToolsBundlePage';
import ToolsDiscountPage from './pages/ToolsDiscountPage';
import ToolInfoPage from './pages/ToolInfoPage';
import ProfileSignInPage from './pages/ProfileSignInPage';
import RVComparePage from './pages/RVComparePage';
import ScrollToTop from './components/ScrollToTop';
import Navigation from './components/Navigation';
import TradeAppraisalForm from './pages/TradeAppraisalForm';
import MobileBottomNav from './components/MobileBottomNav';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import RangerWidget from './components/RangerWidget';
import './App.css';

// Redirect component that preserves query params
const DealerDashboardRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/dealer${location.search}`} replace />;
};

// Legacy /results/:sessionId → new cinematic /my-matches/:sessionId dashboard.
// Preserves any old emails or shared links pointing at the old URL.
const ResultsToMatchesRedirect = () => {
  const { sessionId } = useParams();
  const location = useLocation();
  return <Navigate to={`/my-matches/${sessionId}${location.search}`} replace />;
};

// App content with auth state
const AppContent = () => {
  const [authState, setAuthState] = useState({ isAuthenticated: false, userRole: null });
  
  useEffect(() => {
    // Check for stored auth
    const checkAuth = () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const user = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (token && user) {
        try {
          const userData = JSON.parse(user);
          setAuthState({ 
            isAuthenticated: true, 
            userRole: userData.role || 'user' 
          });
        } catch (e) {
          setAuthState({ isAuthenticated: false, userRole: null });
        }
      }
    };
    checkAuth();
    
    // Listen for auth changes
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  return (
    <>
      <ScrollToTop />
      <Navigation />
      <Routes>
        <Route path="/" element={<LandingPageWithForm />} />
        <Route path="/rigready" element={<RigReadyPage />} />
        <Route path="/embed/application" element={<LandingPageWithForm embedMode={true} />} />
        <Route path="/free/:slug" element={<FreeToolPage />} />
        <Route path="/tools-bundle" element={<ToolsBundlePage />} />
        <Route path="/tools-discount/:sessionId" element={<ToolsDiscountPage />} />
        <Route path="/tool-info/:toolSlug" element={<ToolInfoPage />} />
        <Route path="/my-tool/:productId" element={<MyToolLauncher />} />
        <Route path="/sign-in" element={<ProfileSignInPage />} />
        <Route path="/tool/rv-compare" element={<RVComparePage />} />
        <Route path="/tool/rv-compare/:sessionId" element={<RVComparePage />} />
        <Route path="/quiz" element={<Navigate to="/" replace />} />
        <Route path="/checkout/:productId" element={<ProductCheckoutPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        <Route path="/d/:dealerSlug" element={<RVMatchApplicationForm />} />
        <Route path="/d/:dealerSlug/chat" element={<QuizPage />} />
        <Route path="/:dealerSlug/rv/:shortcode" element={<DealerLinkRedirect />} />
        {/* Dealer-branded landing pages render the SAME main homepage/form
            as `/`, so the buyer's experience is identical to rvmatchai.app.
            The only difference is that LandingPageWithForm reads the
            dealerSlug URL param and stamps every quiz/form/Ranger session
            with that dealer_id so leads are attributed correctly. */}
        <Route path="/:dealerSlug" element={<LandingPageWithForm />} />
        <Route path="/results/:sessionId" element={<ResultsToMatchesRedirect />} />
        <Route path="/my-quiz" element={<MyQuizPage />} />
        <Route path="/my-matches/:sessionId" element={<MyMatchesPage />} />
        <Route path="/favorites" element={<FavoritesPage />} />
        <Route path="/vip/success" element={<VIPSuccessPage />} />
        <Route path="/vip/:sessionId/dashboard" element={<VIPDashboard />} />
        <Route path="/vip/:sessionId/negotiation-coach" element={<NegotiationCoachPage />} />
        <Route path="/vip/:sessionId/deal-analyzer" element={<DealAnalyzerPage />} />
        <Route path="/vip/:sessionId/cost-calculator" element={<CostCalculatorPage />} />
        {/* Dealer routes for VIP tools (no session ID required) */}
        <Route path="/tools/cost-calculator/dealer" element={<CostCalculatorPage />} />
        <Route path="/tools/deal-analyzer/dealer" element={<DealAnalyzerPage />} />
        <Route path="/tools/negotiation-coach/dealer" element={<NegotiationCoachPage />} />
        <Route path="/tools/success" element={<ToolSuccessPage />} />
        <Route path="/tools/cancelled" element={<CostCalculatorPage />} />
        <Route path="/ebook/rv-travel-guide" element={<RVTravelGuideEbook />} />
        <Route path="/ebook/rv-buying-guide" element={<RVBuyingGuideEbook />} />
        <Route path="/ebook/:ebookType" element={<EbookViewerPage />} />
        <Route path="/ebook-viewer/:ebookType" element={<EbookViewerPage />} />
        <Route path="/free-ebook/:ebookType" element={<EbookSqueezePage />} />
        <Route path="/ebook-success/:ebookType" element={<EbookSuccessPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/admin/support" element={<AdminSupportPage />} />
        <Route path="/admin/dealer-emails" element={<AdminDealerEmailPage />} />
        <Route path="/dealer-auth" element={<DealerAuth />} />
        <Route path="/dealer-plans" element={<DealerPlansPage />} />
        <Route path="/dealers" element={<DealerLandingPage />} />
        <Route path="/dashboard" element={<UserDashboard />} />
        <Route path="/dealer" element={<DealerDashboard />} />
        <Route path="/dealer/share-tools" element={<ShareToolsPage />} />
        <Route path="/dealer-dashboard" element={<DealerDashboardRedirect />} />
        <Route path="/dealer/register" element={<DealerRegister />} />
        <Route path="/dealer/inventory" element={<InventoryManager />} />
        <Route path="/dealer/crm" element={<CRMHub />} />
        <Route path="/dealer/research" element={<WebResearch />} />
        <Route path="/dealer/intake-form" element={<CustomerIntakeForm />} />
        <Route path="/intake-form" element={<CustomerIntakeForm />} />
        <Route path="/trade-appraisal" element={<TradeAppraisalForm />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/commerce" element={<AdminCommerceCenter />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
        <Route path="/assist" element={<RVMatchAssist />} />
      </Routes>
      <MobileBottomNav 
        isAuthenticated={authState.isAuthenticated} 
        userRole={authState.userRole} 
      />
      <PWAInstallPrompt />
      <RangerVisibility />
      <Toaster position="top-center" richColors />
    </>
  );
};

/**
 * Ranger only lives on the main RV Match homepage ('/'). It is hidden
 * everywhere else — advisor slug pages, dealer/admin dashboards, buyer
 * dashboards, /rigready, tools, etc.
 */
const RangerVisibility = () => {
  const location = useLocation();
  const path = location.pathname || '';
  if (path !== '/') return null;
  return <RangerWidget />;
};

function App() {
  // Global axios response interceptor — catches 402 "Payment Required"
  // responses from paid-plan-gated endpoints and shows a friendly upgrade
  // toast instead of leaving the caller with a raw error. Any per-caller
  // catch{} still runs after this side-effect fires.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error?.response?.status === 402) {
          const msg = error.response.data?.detail || 'Subscribe to activate this feature.';
          toast.error(msg, {
            duration: 6000,
            action: {
              label: 'See plans',
              onClick: () => { window.location.href = '/dealers'; },
            },
          });
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);
  return (
    <ThemeProvider>
      <NativeAppProvider>
        <div className="App">
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </div>
      </NativeAppProvider>
    </ThemeProvider>
  );
}

export default App;
